import React from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { useTheme } from '../../../../../theming/useTheme';
import { PrimaryLinks } from './PrimaryLinks';
import { Logo, LogoProps } from '../Logo';
import { Editions, EditionDataProps } from './Editions';
import { CtaButton, CtaDataProps } from './CtaButton';
import { HeaderLinksDataProps } from '../Header';
import { Menu } from './menu/Menu';
import { primaryHeaderHeight } from '../header.utils';
import { MenuLinkDataProps } from './menu/links/MenuLink';
import { ErrorBoundary } from '../../../../errorHandling/ErrorBoundary';
import { MEDIA_BREAKPOINTS } from '../../../../../mediaQueries.const';
import { VideoLogo, InjectedVideoLogoApiProps } from './VideoLogo';
import { createStyleForBoxShadow } from '../../../../../theming/boxShadow';
import { BaseUnit } from '../../../../../theming/baseUnitDefinition';

const componentConfigThemeKey = 'primaryHeader';

export interface PrimaryHeaderDataProps {
  links: HeaderLinksDataProps[] | null;
  logo: LogoProps;
  editions: EditionDataProps[] | null;
  cta: CtaDataProps | null;
  menu: MenuLinkDataProps[] | null;
  videoLogo: InjectedVideoLogoApiProps | null;
  pagePath: string;
  editionEndpoint: string;
}

export interface PrimaryHeaderThemeProps {
  baseUnit: BaseUnit;
  backgroundColor: string;
  boxShadowTypeNormal: string;
}

const getStyle = (baseUnit: BaseUnit, backgroundColor: string) => {
  return StyleSheet.create({
    header: {
      backgroundColor,
      position: 'relative',
      height: `${primaryHeaderHeight}px`,
      display: 'flex',
      justifyContent: 'space-between',
      zIndex: 100,
    },
    itemsBasicContainer: {
      display: 'grid',
      gridAutoFlow: 'column',
      height: '100%',
      width: 'fit-content',
      alignItems: 'center',
      gridColumnGap: `${baseUnit}px`,
    },
    itemsRightContainer: {
      [MEDIA_BREAKPOINTS.large]: {
        paddingRight: `${baseUnit}px`,
      },
      [MEDIA_BREAKPOINTS.medium]: {
        paddingRight: `${baseUnit / 2}px`,
      },
      [MEDIA_BREAKPOINTS.small]: {
        paddingRight: `${baseUnit / 2}px`,
      },
    },
    itemsLeftContainer: {
      paddingLeft: `${baseUnit}px`,
    },
  });
};

export const PrimaryHeader: React.FunctionComponent<PrimaryHeaderDataProps> = props => {
  const { links, logo, editions, cta, menu, videoLogo, pagePath, editionEndpoint } = props;
  const { baseUnit, backgroundColor, boxShadowTypeNormal } = useTheme<PrimaryHeaderThemeProps>(componentConfigThemeKey);
  const style = getStyle(baseUnit, backgroundColor);
  const boxShadowStyle = createStyleForBoxShadow({ boxShadowTypeNormal });

  return (
    <ErrorBoundary>
      <div className={css(boxShadowStyle.style, style.header)}>
        <div className={css(style.itemsBasicContainer, style.itemsLeftContainer)}>
          {menu ? <Menu menu={menu} editions={editions} editionEndpoint={editionEndpoint} /> : null}
          {videoLogo ? <VideoLogo {...videoLogo} href={logo.href} /> : <Logo {...logo} />}
          {links ? <PrimaryLinks links={links} pagePath={pagePath} /> : null}
        </div>
        <div className={css(style.itemsBasicContainer, style.itemsRightContainer)}>
          {cta ? <CtaButton {...cta} /> : null}
          {editions ? <Editions editions={editions} editionEndpoint={editionEndpoint} /> : null}
        </div>
      </div>
    </ErrorBoundary>
  );
};
